import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { StatusBadge } from "./Sales";
import { Plus, Check, Truck, CircleSlash, X, BadgeDollarSign } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const PO_STATUS_STYLES = {
  draft: "bg-white text-[#0A0A0A] border-[#0A0A0A]",
  ordered: "bg-[#0033A0] text-white border-[#0033A0]",
  received: "bg-[#0B7A75] text-white border-[#0B7A75]",
  cancelled: "bg-[#495057] text-white border-[#495057]",
};

export default function Procurement() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState("orders");
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [newPO, setNewPO] = useState(false);
  const [newSupplier, setNewSupplier] = useState(false);
  const [viewPO, setViewPO] = useState(null);

  const load = () => Promise.all([
    api.get("/procurement/orders").then(({ data }) => setPos(data)),
    api.get("/procurement/suppliers").then(({ data }) => setSuppliers(data)),
  ]);
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="procurement-page">
      <PageHeader
        title="Procurement"
        subtitle="Suppliers + Purchase Orders. Receiving auto-increments stock and posts to AP ledger."
        action={
          <div className="flex gap-2">
            {tab === "orders" && (
              <button onClick={() => setNewPO(true)} data-testid="new-po-btn"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm font-medium rounded-sm">
                <Plus size={14} /> New PO
              </button>
            )}
            {tab === "suppliers" && (
              <button onClick={() => setNewSupplier(true)} data-testid="new-supplier-btn"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm font-medium rounded-sm">
                <Plus size={14} /> New supplier
              </button>
            )}
          </div>
        }
      />

      <div className="mt-6 flex gap-2 border-b border-[#DEE2E6]">
        <Tab v="orders" cur={tab} onClick={() => setTab("orders")}>Purchase orders</Tab>
        <Tab v="suppliers" cur={tab} onClick={() => setTab("suppliers")}>Suppliers</Tab>
      </div>

      {tab === "orders" && (
        <div className="mt-6 border border-[#0A0A0A] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>Number</Th><Th>Supplier</Th><Th>Status</Th><Th align="right">Total</Th><Th align="right">Paid</Th><Th>Date</Th></tr>
            </thead>
            <tbody>
              {pos.map((p, i) => (
                <tr key={p.id} data-testid={`po-row-${i}`} onClick={() => setViewPO(p)}
                  className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA] cursor-pointer">
                  <Td className="font-mono text-xs font-semibold">{p.number}</Td>
                  <Td>{p.supplier_name}</Td>
                  <Td><span data-testid={`po-status-${p.status}`} className={`inline-block text-[10px] uppercase tracking-[0.2em] border px-2 py-0.5 ${PO_STATUS_STYLES[p.status]}`}>{p.status}</span></Td>
                  <Td align="right" className="tabular-nums">{tenant?.currency || ""} {p.total.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums text-[#0B7A75]">{(p.paid_amount || 0).toLocaleString()}</Td>
                  <Td className="text-[#495057]">{new Date(p.created_at).toLocaleDateString()}</Td>
                </tr>
              ))}
              {pos.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No purchase orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "suppliers" && (
        <div className="mt-6 border border-[#0A0A0A] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th align="right">Outstanding payable</Th><Th>Rating</Th></tr>
            </thead>
            <tbody>
              {suppliers.map((s, i) => (
                <tr key={s.id} data-testid={`supplier-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
                  <Td><div className="font-medium">{s.name}</div></Td>
                  <Td className="text-[#495057]">{s.email || "—"}</Td>
                  <Td className="text-[#495057]">{s.phone || "—"}</Td>
                  <Td align="right" className="tabular-nums font-semibold text-[#D90429]">{tenant?.currency || ""} {(s.balance || 0).toLocaleString()}</Td>
                  <Td>{"★".repeat(s.rating || 0)}{"☆".repeat(5 - (s.rating || 0))}</Td>
                </tr>
              ))}
              {suppliers.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#495057]">No suppliers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {newSupplier && <SupplierForm onClose={() => setNewSupplier(false)} onSaved={() => { setNewSupplier(false); load(); }} />}
      {newPO && <NewPOForm onClose={() => setNewPO(false)} onSaved={() => { setNewPO(false); load(); }} />}
      {viewPO && <PODetail po={viewPO} onClose={() => setViewPO(null)} onChanged={() => { setViewPO(null); load(); }} />}
    </div>
  );
}

function Tab({ v, cur, onClick, children }) {
  return (
    <button onClick={onClick} data-testid={`proc-tab-${v}`}
      className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${cur === v ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057] hover:text-[#0A0A0A]"}`}>
      {children}
    </button>
  );
}

function SupplierForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", tax_id: "", rating: 0 });
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/procurement/suppliers", { ...f, rating: parseInt(f.rating || 0) }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <Modal title="New supplier" onClose={onClose} testid="supplier-modal">
      <form onSubmit={save} className="space-y-4" data-testid="supplier-form">
        <Field label="Name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="supplier-name" />
        <Field label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="supplier-email" />
        <Field label="Phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="supplier-phone" />
        <Field label="Tax ID" value={f.tax_id} onChange={(v) => setF({ ...f, tax_id: v })} testid="supplier-tax" />
        <Field label="Rating (0-5)" type="number" value={f.rating} onChange={(v) => setF({ ...f, rating: v })} testid="supplier-rating" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="supplier-save" className="px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#FF4F00] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

function NewPOForm({ onClose, onSaved }) {
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [lines, setLines] = useState([{ item_id: "", qty: 1, unit_cost: 0 }]);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    Promise.all([api.get("/procurement/suppliers"), api.get("/items")])
      .then(([s, i]) => { setSuppliers(s.data); setItems(i.data); });
  }, []);
  const updateLine = (i, patch) => setLines((l) => l.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln));
  const onItem = (i, item_id) => {
    const it = items.find((x) => x.id === item_id);
    updateLine(i, { item_id, unit_cost: it?.cost ?? 0 });
  };
  const total = lines.reduce((s, l) => s + parseFloat(l.qty || 0) * parseFloat(l.unit_cost || 0), 0);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/procurement/orders", {
        supplier_id: supplier,
        lines: lines.filter((l) => l.item_id && l.qty > 0).map((l) => ({
          item_id: l.item_id, qty: parseFloat(l.qty), unit_cost: parseFloat(l.unit_cost || 0),
        })),
        notes,
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <Modal title="New purchase order" onClose={onClose} testid="new-po-modal" wide>
      <form onSubmit={save} className="space-y-5" data-testid="new-po-form">
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Supplier</span>
          <select required value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="po-supplier"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm focus:border-[#0A0A0A] focus:outline-none">
            <option value="">— pick supplier —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Lines</div>
          <div className="border border-[#DEE2E6]">
            <div className="grid grid-cols-[1fr_80px_120px_100px_30px] gap-2 px-3 py-2 bg-[#F8F9FA] text-[10px] uppercase tracking-[0.15em] text-[#495057]">
              <div>Item</div><div className="text-right">Qty</div><div className="text-right">Unit cost</div><div className="text-right">Line total</div><div></div>
            </div>
            {lines.map((ln, i) => (
              <div key={i} data-testid={`po-line-${i}`} className="grid grid-cols-[1fr_80px_120px_100px_30px] gap-2 px-3 py-2 border-t border-[#DEE2E6] items-center">
                <select required value={ln.item_id} onChange={(e) => onItem(i, e.target.value)} data-testid={`po-line-item-${i}`} className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm">
                  <option value="">— pick item —</option>
                  {items.map((x) => <option key={x.id} value={x.id}>{x.sku} · {x.name}</option>)}
                </select>
                <input type="number" min="0.01" step="0.01" required value={ln.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} data-testid={`po-line-qty-${i}`} className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm text-right" />
                <input type="number" min="0" step="0.01" required value={ln.unit_cost} onChange={(e) => updateLine(i, { unit_cost: e.target.value })} data-testid={`po-line-cost-${i}`} className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm text-right" />
                <div className="text-right text-sm tabular-nums">{(parseFloat(ln.qty || 0) * parseFloat(ln.unit_cost || 0)).toFixed(2)}</div>
                <button type="button" onClick={() => setLines((l) => l.filter((_, x) => x !== i))} className="text-[#D90429] p-1"><X size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={() => setLines((l) => [...l, { item_id: "", qty: 1, unit_cost: 0 }])} data-testid="po-add-line"
              className="w-full px-3 py-2 text-xs text-[#0033A0] hover:bg-[#F8F9FA] border-t border-[#DEE2E6] text-left">+ Add line</button>
          </div>
        </div>
        <div className="text-right text-sm">Subtotal: <span className="font-bold tabular-nums" data-testid="po-subtotal">{total.toFixed(2)}</span></div>
        <Field label="Notes" value={notes} onChange={setNotes} testid="po-notes" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="po-save" className="px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#FF4F00] text-white">Save draft</button>
        </div>
      </form>
    </Modal>
  );
}

function PODetail({ po: initial, onClose, onChanged }) {
  const { tenant } = useAuth();
  const [po, setPo] = useState(initial);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const refresh = async () => setPo((await api.get(`/procurement/orders/${po.id}`)).data);
  const action = async (path) => {
    setBusy(true); setErr("");
    try { await api.post(`/procurement/orders/${po.id}/${path}`); await refresh(); onChanged?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`${po.number} · ${po.supplier_name}`} onClose={onClose} testid="po-detail-modal" wide>
      <div className="space-y-5" data-testid="po-detail">
        <div className="flex items-center gap-3"><span className={`inline-block text-[10px] uppercase tracking-[0.2em] border px-2 py-0.5 ${PO_STATUS_STYLES[po.status]}`}>{po.status}</span></div>
        <table className="w-full text-sm border border-[#DEE2E6]">
          <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-[0.15em] text-[#495057]">
            <tr><Th>SKU</Th><Th>Name</Th><Th align="right">Qty</Th><Th align="right">Unit</Th><Th align="right">Line total</Th></tr>
          </thead>
          <tbody>
            {po.lines.map((l, i) => (
              <tr key={i} className="border-t border-[#DEE2E6]">
                <Td className="font-mono text-xs">{l.sku}</Td><Td>{l.name}</Td>
                <Td align="right" className="tabular-nums">{l.qty}</Td>
                <Td align="right" className="tabular-nums">{l.unit_cost.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums">{(l.qty * l.unit_cost).toFixed(2)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-right text-sm">
          <div>Subtotal: <span className="tabular-nums">{po.subtotal.toLocaleString()}</span></div>
          <div>Tax ({po.tax_pct}%): <span className="tabular-nums">{po.tax_amount.toLocaleString()}</span></div>
          <div className="font-bold">Total: <span className="tabular-nums">{tenant?.currency || ""} {po.total.toLocaleString()}</span></div>
          {po.grn_number && <div className="text-[#0B7A75] mt-1">GRN: {po.grn_number}</div>}
        </div>
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-[#DEE2E6]">
          {po.status === "draft" && (
            <>
              <button onClick={() => action("cancel")} disabled={busy} data-testid="po-cancel-btn"
                className="px-4 py-2 text-sm border border-[#D90429] text-[#D90429] inline-flex items-center gap-1.5"><CircleSlash size={14} /> Cancel</button>
              <button onClick={() => action("order")} disabled={busy} data-testid="po-order-btn"
                className="px-4 py-2 text-sm bg-[#0033A0] text-white inline-flex items-center gap-1.5"><Check size={14} /> Submit order</button>
              <button onClick={() => action("receive")} disabled={busy} data-testid="po-receive-btn"
                className="px-4 py-2 text-sm bg-[#0B7A75] text-white inline-flex items-center gap-1.5"><Truck size={14} /> Receive (auto-stock)</button>
            </>
          )}
          {po.status === "ordered" && (
            <button onClick={() => action("receive")} disabled={busy} data-testid="po-receive-btn"
              className="px-4 py-2 text-sm bg-[#0B7A75] text-white inline-flex items-center gap-1.5"><Truck size={14} /> Receive (auto-stock)</button>
          )}
          {po.status === "received" && (
            <button onClick={() => setPayOpen(true)} disabled={busy} data-testid="po-pay-btn"
              className="px-4 py-2 text-sm bg-[#0033A0] text-white inline-flex items-center gap-1.5"><BadgeDollarSign size={14} /> Pay supplier</button>
          )}
        </div>
      </div>
      {payOpen && <PayForm po={po} onClose={() => setPayOpen(false)} onSaved={async () => { setPayOpen(false); await refresh(); onChanged?.(); }} />}
    </Modal>
  );
}

function PayForm({ po, onClose, onSaved }) {
  const outstanding = po.total - (po.paid_amount || 0);
  const [amount, setAmount] = useState(outstanding);
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/procurement/payments", { purchase_order_id: po.id, amount: parseFloat(amount) }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title={`Pay supplier · ${po.number}`} onClose={onClose} testid="po-pay-modal">
      <form onSubmit={save} className="space-y-4">
        <Field label={`Amount (outstanding ${outstanding.toFixed(2)})`} type="number" required value={amount} onChange={setAmount} testid="po-pay-amount" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="po-pay-save" className="px-4 py-2 text-sm bg-[#0033A0] text-white">Record</button>
        </div>
      </form>
    </Modal>
  );
}
